import { getTimeZone } from 'util/time';
import { isP2pCall } from 'util/getCallType';
import { VcsEgressExternalControllerV3Service } from 'api';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@web-chat-core/ui-kit';
import { SummaryMetadataBody } from 'api/types';
import { FEATURES } from 'consts';
import { summaryStore } from 'state/store';
import { SUMMARY_STATUS } from 'state/store/summary/types';
import {
    selectSummaryStatus,
    selectRoomId,
    selectUserId,
    selectSummaryParticipants,
    selectIsMeSubscribedToSummary,
    selectStoppedByServer,
    selectLocalParticipant,
    selectParticipants,
    selectIsSummaryHasBeenInitByMe,
} from 'state/selectors';
import { isModeratorParticipant } from 'useCase';
import { isFeatureOn } from 'useCase/featureFlags/featureFlagsApi';

interface UseSummaryResult {
    startSummarizer(): void;
    stopSummarizer(): void;
    updateSummaryData(metadata: SummaryMetadataBody | null): void;
    checkOnUnavailableSummarize(): boolean;
}

export const useSummary = (): UseSummaryResult => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isVcsModeratorsEnabled = isFeatureOn(FEATURES.VCS_MODERATORS_ENABLED);
    const showSummarySnackbar = isFeatureOn(FEATURES.VCS_AI_RECAP_ENABLED) ? showSnackbar : noop;

    const startSummarizer = async () => {
        summaryStore.setWaitingForStartStatus();

        const roomId = selectRoomId();
        const timeZone = getTimeZone();
        const identityId = selectUserId();

        if (!identityId) {
            showSummarySnackbar(t('web_videoconference_error'), 'error');

            summaryStore.setInitialStatus();
            return;
        }

        const { error, payload } = await VcsEgressExternalControllerV3Service.startAudioEgress(-1, {
            roomId,
            timeZone,
            identityId: identityId.toString(),
        });

        if (payload) {
            summaryStore.setIsInitByMe(true);
            console.log('Расшифровка записи начнётся при получении метаданный комнаты о старте записи');
        }

        if (error) {
            showSummarySnackbar(t('web_videoconference_error'), 'error');

            summaryStore.setInitialStatus();
        }
    };

    const stopSummarizer = async () => {
        const roomId = selectRoomId();

        const { error, payload } = await VcsEgressExternalControllerV3Service.stopAudioEgress(-1, {
            roomId,
        });

        if (payload) {
            console.log('Попытка остановить расшифровку записи');
        }
        if (error) {
            showSummarySnackbar(t('error_performing_action'), 'error');
        }
    };

    const updateSummaryData = (metadata: SummaryMetadataBody | null) => {
        const isCooldownRecording = Boolean(metadata?.coolDownRecording);
        const isStoppedByServer = metadata && 'stoppedByServer' in metadata && metadata.stoppedByServer;
        const summaryStatus = selectSummaryStatus();
        const previousParticipants = selectSummaryParticipants();
        const newParticipants = metadata && 'participants' in metadata ? metadata.participants : null;
        const userId = selectUserId();
        const isMeInPreviousParticipants = selectIsMeSubscribedToSummary();
        const isMeInNewParticipants = !isNil(userId) && !isNil(newParticipants) && Boolean(newParticipants?.[userId]);

        summaryStore.setIsInitByMe(isMeInNewParticipants);

        // Сервер останавливает ассистента (возможно по таймауту)
        if (isCooldownRecording && isStoppedByServer) {
            summaryStore.setStoppedByServer(true);
            summaryStore.setStoppingStatus();
            return;
        }

        if (summaryStatus === SUMMARY_STATUS.INITIAL) {
            // Получаем сразу участников, если кто-то другой вкл ассистента
            if (newParticipants) {
                showSummarySnackbar(t('videoconference_AI_Assistant_turned_on'));

                summaryStore.setParticipants(newParticipants);
                summaryStore.setSummarizingStatus();
                return;
            }
        }

        if (summaryStatus === SUMMARY_STATUS.WAITING_FOR_START) {
            if (!previousParticipants && !newParticipants) return;

            if (previousParticipants && !newParticipants) {
                summaryStore.setStoppingStatus();
            }

            const isMeEnableSummary = userId && !isMeInPreviousParticipants && isMeInNewParticipants;

            if (isMeEnableSummary) {
                // Я включил ассистента
                showSummarySnackbar(t('videoconference_AI_Assistant_turn_on_snack'));
            }

            summaryStore.setParticipants(newParticipants);

            // Если получили участников - значит началась суммаризация
            summaryStore.setSummarizingStatus();
            return;
        }

        if (summaryStatus === SUMMARY_STATUS.SUMMARIZING) {
            if (userId) {
                if (isMeInPreviousParticipants && !isMeInNewParticipants) {
                    // Если меня нет в текущих участниках - значит я отписался от ассистента
                    showSummarySnackbar(t('videoconference_AI_Assistant_turn_off_snack'));
                } else if (!isMeInPreviousParticipants && isMeInNewParticipants) {
                    // Я включил ассистента
                    showSummarySnackbar(t('videoconference_AI_Assistant_turn_on_snack'));
                } else {
                    // Кто-то другой вкл/откл ассистента
                    // Будет необходимо в третьей итерации
                }
            }

            if (isCooldownRecording && !newParticipants) {
                summaryStore.setStoppingStatus();
                return;
            }

            summaryStore.setParticipants(newParticipants);
            return;
        }

        if (summaryStatus === SUMMARY_STATUS.STOPPING) {
            const isPreviousStoppedByServer = selectStoppedByServer();

            if (!metadata?.coolDownRecording) {
                if (isPreviousStoppedByServer) {
                    // Остановка сервером, показать всем одинаковый снекбар
                    showSummarySnackbar(t('videoconference_AI_Assistant_turned_off'));
                } else if (!isMeInPreviousParticipants) {
                    // Мне уже показан снекбар об остановке ассистента, показать другим
                    showSummarySnackbar(t('videoconference_AI_Assistant_turned_off'));
                }

                summaryStore.setStoppedByServer(null);
                summaryStore.setInitialStatus();
                summaryStore.setParticipants(null);
                return;
            }
        }
    };

    const checkOnUnavailableSummarize = (): boolean => {
        const isP2p = isP2pCall();
        const localParticipant = selectLocalParticipant();
        const isModerator = isVcsModeratorsEnabled && localParticipant && isModeratorParticipant(localParticipant);
        if (!isP2p) return !isModerator;

        const participants = selectParticipants();
        const status = selectSummaryStatus();
        const isInitByMe = selectIsSummaryHasBeenInitByMe();
        const moreThanTwo = participants.length > 2;
        const isInitial = status === SUMMARY_STATUS.INITIAL;

        return moreThanTwo && !isModerator && (isInitial || !isInitByMe);
    };

    return {
        startSummarizer,
        stopSummarizer,
        updateSummaryData,
        checkOnUnavailableSummarize,
    };
};
